import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Loader2, Navigation, AlertCircle, Phone, Globe } from 'lucide-react';
import { publicApi, resolveImage } from '../api';
import ActivityMap from '../components/ActivityMap';

export default function Nearby() {
  const [coords, setCoords] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [radius, setRadius] = useState(5);
  const [category, setCategory] = useState('');

  const askLocation = () => {
    setError(null);
    if (!navigator.geolocation) {
      setError("Il tuo browser non supporta la geolocalizzazione.");
      return;
    }
    if (!window.isSecureContext) {
      setError("La geolocalizzazione richiede una connessione sicura (HTTPS). Apri il sito con https://");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLoading(false);
      },
      err => {
        setLoading(false);
        // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        if (err.code === 1) {
          setError("Hai negato l'accesso alla posizione. Vai nelle impostazioni del browser (icona lucchetto vicino all'URL) e riattiva 'Posizione' per questo sito.");
        } else if (err.code === 2) {
          setError("Posizione non disponibile. Verifica che il GPS del telefono sia attivo.");
        } else if (err.code === 3) {
          setError("Tempo scaduto nel rilevamento posizione. Riprova all'aperto o con GPS attivo.");
        } else {
          setError("Impossibile ottenere la tua posizione: " + err.message);
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  useEffect(() => {
    if (!coords) return;
    setLoading(true);
    publicApi.nearby(coords.lat, coords.lng, {
      radius_km: radius, ...(category && { category }),
    })
      .then(setData)
      .catch(e => setError('Errore ricerca: ' + e.message))
      .finally(() => setLoading(false));
  }, [coords, radius, category]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-14" data-testid="nearby-page">
      <div className="flex items-center gap-3 mb-2">
        <Navigation size={24} style={{ color: 'var(--yup-primary)' }} />
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold" style={{ color: 'var(--yup-primary)' }}>
          Vicino a te
        </h1>
      </div>
      <p className="text-white/70 text-sm mb-6 sm:mb-8">
        Attività, servizi e punti di interesse nelle vicinanze della tua posizione attuale.
      </p>

      {!coords && !error && (
        <div className="bg-[color:var(--yup-card)] border border-[color:var(--yup-border)] rounded-2xl p-6 sm:p-10 text-center">
          <MapPin size={48} className="mx-auto mb-4" style={{ color: 'var(--yup-primary)' }} />
          <h2 className="text-white text-lg sm:text-xl font-semibold mb-2">Trova cosa c'è intorno a te</h2>
          <p className="text-white/70 text-sm mb-6 max-w-md mx-auto">
            Concedi l'accesso alla tua posizione: ti mostriamo ristoranti, noleggi, servizi e attrazioni in un raggio a tua scelta.
          </p>
          <button
            onClick={askLocation}
            disabled={loading}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-bold text-black shadow-xl hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}
            data-testid="nearby-locate-btn"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Navigation size={18} />}
            Usa la mia posizione
          </button>
          <p className="text-white/40 text-xs mt-4">
            Il tuo browser ti chiederà l'autorizzazione. Nessun dato viene salvato.
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-300 flex items-start gap-3 mb-6">
          <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Errore</div>
            <div className="text-sm">{error}</div>
            <button onClick={askLocation} className="mt-3 text-red-200 underline text-sm">
              Riprova
            </button>
          </div>
        </div>
      )}

      {coords && (
        <>
          <div className="flex flex-wrap gap-3 mb-6 items-center bg-white/5 border border-white/10 rounded-xl p-3 sm:p-4">
            <div className="w-full sm:w-auto text-xs sm:text-sm text-white/60">
              Posizione: <span className="text-[color:var(--yup-primary)] font-mono">{coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}</span>
            </div>
            <select
              value={radius}
              onChange={e => setRadius(Number(e.target.value))}
              className="flex-1 sm:flex-none bg-black/40 border border-[color:var(--yup-border)] text-white text-base sm:text-sm rounded-lg px-3 py-2.5"
              data-testid="nearby-radius-select"
            >
              <option value="1">📍 1 km</option>
              <option value="3">📍 3 km</option>
              <option value="5">📍 5 km</option>
              <option value="10">📍 10 km</option>
              <option value="25">📍 25 km</option>
              <option value="50">📍 50 km</option>
            </select>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="flex-1 sm:flex-none bg-black/40 border border-[color:var(--yup-border)] text-white text-base sm:text-sm rounded-lg px-3 py-2.5"
              data-testid="nearby-category-select"
            >
              <option value="">Tutte le categorie</option>
              {['ristoranti','divertimento','sport','cultura','natura','relax','emergenze',
                'trasporti','noleggi','supermercati','bancomat','partner','tour','shopping-center'].map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <button onClick={askLocation} className="text-sm text-[color:var(--yup-primary)] hover:underline w-full sm:w-auto sm:ml-auto text-center sm:text-right py-2">
              🔄 Aggiorna posizione
            </button>
          </div>

          {loading && (
            <div className="flex justify-center py-16">
              <Loader2 size={32} className="animate-spin text-white/50" />
            </div>
          )}

          {!loading && data && (
            <>
              <div className="text-sm text-white/70 mb-3">
                <strong className="text-white">{data.count}</strong> attività entro <strong className="text-white">{radius} km</strong>
              </div>

              {data.results.length > 0 && (
                <>
                  <ActivityMap
                    activities={[...data.results, { id: '_me', name: 'Sei qui', latitude: coords.lat, longitude: coords.lng }]}
                    height="380px"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-testid="nearby-grid">
                    {data.results.map(a => (
                      <div key={a.id} className="bg-[color:var(--yup-card)] border border-[color:var(--yup-border)] rounded-lg overflow-hidden hover:border-[color:var(--yup-primary)] transition"
                           data-testid={`nearby-item-${a.slug || a.id}`}>
                        {a.image && <img src={resolveImage(a.image)} alt={a.name} className="w-full h-36 object-cover" />}
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs uppercase tracking-widest text-[color:var(--yup-primary)]">
                              {a.category_key}{a.subcategory ? ` · ${a.subcategory}` : ''}
                            </span>
                            <span className="text-xs bg-black/40 border border-[color:var(--yup-primary)]/30 px-2 py-0.5 rounded text-[color:var(--yup-primary)] font-mono">
                              {a.distance_km} km
                            </span>
                          </div>
                          <h3 className="text-white font-semibold text-lg">{a.name}</h3>
                          {a.address && (
                            <div className="text-sm text-white/60 mt-1 flex items-start gap-1.5">
                              <MapPin size={13} className="mt-0.5 flex-shrink-0" /> {a.address}
                            </div>
                          )}
                          {a.phone && (
                            <a href={`tel:${a.phone}`} className="text-sm text-white/70 hover:text-[color:var(--yup-primary)] mt-1 flex items-center gap-1.5">
                              <Phone size={13} /> {a.phone}
                            </a>
                          )}
                          {a.website && (
                            <a href={a.website} target="_blank" rel="noopener noreferrer" className="text-sm text-white/70 hover:text-[color:var(--yup-primary)] flex items-center gap-1.5 mt-1">
                              <Globe size={13} /> Sito
                            </a>
                          )}
                          <Link to={`/${a.city_slug}/cosa-vedere/${a.category_key}${a.subcategory ? `/${a.subcategory}` : ''}`}
                                className="mt-3 text-xs text-[color:var(--yup-primary)] hover:underline block">
                            Vedi tutte in {a.category_key} →
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {data.results.length === 0 && (
                <div className="text-center py-16 text-white/60">
                  Nessuna attività trovata entro {radius} km. Prova ad ampliare il raggio.
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
